import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { Column } from 'components/Layout';
import Row from 'components/Layout/Row';
import Avatar from 'further-ui/components/Avatar';
import Table, { type Column as TableColumn } from 'further-ui/components/Table';
import TypographyButton from 'further-ui/components/TypographyButton';
import {
  numberToCurrencyString,
  numberToDisplayString,
  numberToPercentageString,
  pluralize,
} from 'further-ui/utils';
import { useState } from 'react';
import { CommitmentData } from '../../types';

const AssetRow = styled(Row)(({ theme }) => ({
  padding: theme.spacing(theme.spacingSizes.xs, 0),
  margin: `-${theme.spacing(theme.spacingSizes.xs)} 0`,
}));

type Props = Pick<CommitmentData, 'currencySettings'> & {
  holdingsByInvestment: CommitmentData['liveHoldings']['byInvestment'];
};

const LiveHoldingsByInvestment: React.FC<Props> = ({
  currencySettings,
  holdingsByInvestment: { data, totals },
}) => {
  const [expanded, setExpanded] = useState(false);
  const { currencyLcy } = currencySettings;

  const columns: Array<TableColumn<(typeof data)[number]>> = [
    {
      label: 'Asset',
      key: 'assetName',
      paddingY: 0,
      width: 240,
      render: ({ assetName, assetImage }) => (
        <AssetRow centered spacing="xs">
          <Avatar color="random" text={assetName} size="lg" src={assetImage} />
          <Typography variant="inherit">{assetName}</Typography>
        </AssetRow>
      ),
      footer: (
        <>
          Total from {data.length}{' '}
          {pluralize('investment', data.length, 'investments')}
        </>
      ),
    },
    {
      label: 'Share class',
      key: 'shareClassName',
      width: 200,
      render: ({ shareClassName }) => shareClassName,
    },
    {
      label: 'Investment date',
      key: 'investmentDate',
      width: 140,
      render: ({ investmentDate }) =>
        investmentDate.toLabel(undefined, 'MMM dd, yyyy'),
    },
    {
      label: `Invested capital ${currencyLcy}`,
      key: 'investedCapitalLcy',
      width: 180,
      render: ({ investedCapitalLcy }) =>
        numberToCurrencyString(investedCapitalLcy, {
          currency: currencyLcy,
        }),
      footer: numberToCurrencyString(totals.investedCapitalLcy, {
        currency: currencyLcy,
      }),
    },
    {
      label: `Current hold value ${currencyLcy}`,
      key: 'currentHoldingValueLcy',
      width: 180,
      render: ({ currentHoldingValueLcy }) =>
        numberToCurrencyString(currentHoldingValueLcy, {
          currency: currencyLcy,
        }),
    },
    {
      label: 'Gross MOIC',
      key: 'grossMoic',
      width: 120,
      render: ({ grossMoic }) => `${grossMoic}x`,
      footer: `${totals.grossMoic}x`,
    },
    {
      label: 'Gross IRR',
      key: 'grossIrr',
      width: 120,
      render: ({ grossIrr }) =>
        numberToPercentageString(grossIrr, { accuracy: 2 }),
      footer: numberToPercentageString(totals.grossIrr, {
        accuracy: 2,
      }),
    },
    ...(expanded
      ? [
          {
            label: 'Pro-rated holding units',
            key: 'proRatedHoldingUnits',
            width: 200,
            render: ({ proRatedHoldingUnits }) =>
              numberToDisplayString(proRatedHoldingUnits),
          },
          {
            label: `Initial unit value ${currencyLcy}`,
            key: 'initialUnitValueLcy',
            width: 200,
            render: ({ initialUnitValueLcy }) =>
              numberToCurrencyString(initialUnitValueLcy, {
                currency: currencyLcy,
              }),
          },
          {
            label: `Present unit value ${currencyLcy}`,
            key: 'presentUnitValueLcy',
            width: 200,
            render: ({ presentUnitValueLcy }) =>
              numberToCurrencyString(presentUnitValueLcy, {
                currency: currencyLcy,
              }),
          },
        ]
      : []),
  ];

  return (
    <Column spacing="md">
      <Table
        scrollable
        emptyMessage="No holdings"
        columns={columns}
        rows={data}
      />
      <Row justify="flex-end">
        <TypographyButton
          variant="body2"
          text={expanded ? 'Collapse table' : 'Expand table'}
          onClick={() => setExpanded((prev) => !prev)}
        />
      </Row>
    </Column>
  );
};

export default LiveHoldingsByInvestment;
